import type jsQR from 'jsqr'

interface DetectedBarcode {
  rawValue: string
}

interface NativeDetector {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>
}

interface NativeDetectorConstructor {
  new (options: { formats: string[] }): NativeDetector
  getSupportedFormats(): Promise<string[]>
}

type JsQr = typeof jsQR
type Inversion = 'dontInvert' | 'attemptBoth'

const CAMERA_FRAME_SIDE = 640
const PHOTO_SIDES = [1280, 800]

let nativeDetector: Promise<NativeDetector | null> | null = null
let jsQrLoading: Promise<JsQr> | null = null

function loadNativeDetector(): Promise<NativeDetector | null> {
  nativeDetector ??= (async () => {
    const Detector = (globalThis as { BarcodeDetector?: NativeDetectorConstructor }).BarcodeDetector
    if (!Detector) return null
    try {
      const formats = await Detector.getSupportedFormats()
      return formats.includes('qr_code') ? new Detector({ formats: ['qr_code'] }) : null
    } catch {
      return null
    }
  })()
  return nativeDetector
}

function loadJsQr(): Promise<JsQr> {
  jsQrLoading ??= import('jsqr').then((module) => module.default)
  return jsQrLoading
}

function decodeWithJsQr(
  decode: JsQr,
  source: CanvasImageSource,
  width: number,
  height: number,
  maxSide: number,
  canvas: HTMLCanvasElement,
  inversion: Inversion,
): string | null {
  const scale = Math.min(1, maxSide / Math.max(width, height))
  canvas.width = Math.max(1, Math.round(width * scale))
  canvas.height = Math.max(1, Math.round(height * scale))
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return null
  context.drawImage(source, 0, 0, canvas.width, canvas.height)
  const image = context.getImageData(0, 0, canvas.width, canvas.height)
  return decode(image.data, image.width, image.height, { inversionAttempts: inversion })?.data ?? null
}

async function detectNatively(source: CanvasImageSource): Promise<string | null> {
  const detector = await loadNativeDetector()
  if (!detector) return null
  try {
    const [first] = await detector.detect(source)
    return first?.rawValue ?? null
  } catch {
    return null
  }
}

export function preloadQrDecoder(): void {
  void loadNativeDetector().then((detector) => (detector ? null : loadJsQr()))
}

export async function decodeVideoFrame(video: HTMLVideoElement, canvas: HTMLCanvasElement): Promise<string | null> {
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || video.videoWidth === 0) return null
  if (await loadNativeDetector()) return detectNatively(video)
  const decode = await loadJsQr()
  return decodeWithJsQr(decode, video, video.videoWidth, video.videoHeight, CAMERA_FRAME_SIDE, canvas, 'dontInvert')
}

export async function decodeImageFile(file: Blob): Promise<string | null> {
  const image = await loadImage(file)
  try {
    const native = await detectNatively(image.source)
    if (native) return native
    const decode = await loadJsQr()
    const canvas = document.createElement('canvas')
    for (const side of PHOTO_SIDES) {
      const text = decodeWithJsQr(decode, image.source, image.width, image.height, side, canvas, 'attemptBoth')
      if (text) return text
    }
    return null
  } finally {
    image.release()
  }
}

interface LoadedImage {
  source: CanvasImageSource
  width: number
  height: number
  release: () => void
}

async function loadImage(file: Blob): Promise<LoadedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file)
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() }
    } catch {
      // HEIC kabi formatlarni ba'zi brauzerlar faqat <img> orqali ochadi
    }
  }
  const url = URL.createObjectURL(file)
  try {
    const element = new Image()
    element.src = url
    await element.decode()
    return {
      source: element,
      width: element.naturalWidth,
      height: element.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    }
  } catch (error) {
    URL.revokeObjectURL(url)
    throw error
  }
}
